import moment from "moment/min/moment-with-locales";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Stack, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { Await, useRouteLoaderData } from "react-router-dom";
import DataDescription from "../components/DataDescription";
import { getDemoName, getRadioName } from "../components/HelperFunctions";
import {
    useFetchData,
    useLatestCalculatedDate,
    useResolvedRadios,
    useSetDaypartOnChange,
    useSetDaysOnPerspectiveChange,
    useSetPerspectiveOnDaysChange,
} from "../components/HelperHooks";
import LoadingIndicator from "../components/LoadingIndicator";
import PageDesciption from "../components/PageDesciption";
import DatesPicker, {
    latest2Weeks,
    latestFull2Weeks,
    latestFull4Weeks,
    latestFullWeek,
    latestMonth,
    latestQuarter,
    latestWeek,
    latestYear,
} from "../components/Pickers/DatesPicker";
import DaypartPicker from "../components/Pickers/DaypartPicker";
import DaypartPickerTsl from "../components/Pickers/DaypartPickerTsl";
import DaysPicker from "../components/Pickers/DaysPicker";
import DemoPicker from "../components/Pickers/DemoPicker";
import HoursPicker from "../components/Pickers/HoursPicker";
import PerspectivePicker from "../components/Pickers/PerspectivePicker";
import StationPicker from "../components/Pickers/StationPicker";
import { useUserFormState } from "../components/UserSelection";
import { DataContent, DescriptionWithDownloadRow, InputSelection, ModulePage } from "../components/pageSections";
import RATooltip from "../components/RATooltip";
import TslChart from "../components/TslChart";

const extraStationsCount = 3;
const extraDefaults = Object.assign(
    {},
    ...Array(extraStationsCount)
        .fill()
        .map((_, i) => ({ [`extraStation${i}`]: [], [`extraDemo${i}`]: [], [`extraDaypart${i}`]: [] }))
);

export default function TSL() {
    const moduleName = useMemo(() => "tsl", []);
    const moduleId = useMemo(() => ({ 1: 3, 2: 19 }), []);
    const { demographics, userInfo, radiosModuleSettings } = useRouteLoaderData("root");

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName, extraDefaults);

    const [latestCalculatedDate, setLatestCalculatedDate] = useState(null);
    const [lastYearAverages, setLastYearAverages] = useState(false);
    const [moduleSelection, setModuleSelection] = useState(0); // 0 = sessions, 1 = listener
    const [appliedModuleSelection, setAppliedModuleSelection] = useState(0);
    const [grouping, setGrouping] = useState(0);
    const [excludeMode, setExcludeMode] = useState("both");
    const [allowedGroupings, setAllowedGroupings] = useState([]);
    const [loading, errorCode, data, fetchData] = useFetchData();
    const [subLoading, setSubLoading] = useState(false);
    const resolvedRadios = useResolvedRadios();
    const [resolvedDemos, setResolvedDemos] = useState(null);
    const container = useRef(null);
    const advancedInputRef = useRef(null);
    const [fileName, setFileName] = useState("TSL Data");

    const predefinedRanges = useMemo(() => {
        if (latestCalculatedDate) {
            if (formData.dateRange === undefined) {
                setFormData(current => ({ ...current, dateRange: latestMonth(latestCalculatedDate, true) }));
            }
            return [
                latestWeek(latestCalculatedDate),
                latest2Weeks(latestCalculatedDate),
                latestMonth(latestCalculatedDate),
                latestQuarter(latestCalculatedDate),
                latestYear(latestCalculatedDate),
                latestFullWeek(latestCalculatedDate),
                latestFull2Weeks(latestCalculatedDate),
                latestFull4Weeks(latestCalculatedDate),
            ];
        }
    }, [formData.dateRange, latestCalculatedDate, setFormData]);

    useEffect(() => {
        async function resolveDemos() {
            setResolvedDemos((await demographics).data);
        }
        resolveDemos();
    }, [demographics]);

    const csvHeaders = useMemo(() => {
        if (!resolvedDemos || !resolvedRadios || !data?.data || !formData.dateRange) return;
        let radioNames = data.data.map(x => getRadioName(x.radio.radio_id, resolvedRadios));

        const start = moment(formData.dateRange[0]).format("DD-MM-YYYY");
        const end = moment(formData.dateRange[1]).format("DD-MM-YYYY");

        setFileName(`TSL ${radioNames.join(" and ")} from ${start} to ${end}`);

        const headers = [
            "Basis Datetime",
            ...data.data.map(x => `${getRadioName(x.radio.radio_id, resolvedRadios)} - ${getDemoName(x.radio.demo_id, resolvedDemos)}`),
        ].concat(`${getRadioName(data.data[0].radio.radio_id, resolvedRadios)} - ${getDemoName(data.data[0].radio.demo_id, resolvedDemos)} (AVG)`);

        return headers;
    }, [data, formData.dateRange, resolvedDemos, resolvedRadios]);

    const csvData = useMemo(() => {
        if (!data?.data || data.data.length === 0) return [];

        const formatTime = seconds => moment.utc(moment.duration(seconds, "seconds").asMilliseconds()).format("HH:mm:ss");

        return data.data[0].tsl.map((row, i) => {
            const basisHour = row.basis_hour;

            const tslValues = data.data.map(radio => (radio.tsl[i]?.tsl != null ? radio.tsl[i].tsl : null));
            const formattedTslValues = tslValues.map(v => (v != null ? formatTime(v) : "-"));
            const validTsl = tslValues.filter(v => v != null);
            const average = validTsl.length ? formatTime(Math.round(validTsl.reduce((sum, val) => sum + val, 0) / validTsl.length)) : "-";

            return [basisHour, ...formattedTslValues, average];
        });
    }, [data?.data]);

    useSetPerspectiveOnDaysChange(setFormData, formData.days);
    useSetDaysOnPerspectiveChange(setFormData, formData.perspective);
    useLatestCalculatedDate(setLatestCalculatedDate, moduleId, formData.station, formData.demo);
    useSetDaypartOnChange(setFormData, formData.station, formData.days, formData.hourRange);

    function generateGrouping(dateRange, module) {
        if (!dateRange) return { allowed: [], def: 0 };

        const diffMs = Math.abs(dateRange[1] - dateRange[0]);
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        if (module === 1) {
            return { allowed: [4, 5], def: 4 };
        }

        if (diffDays <= 7) return { allowed: [1, 2, 3], def: 1 };
        if (diffDays <= 14) return { allowed: [1, 2, 3, 4], def: 1 };
        if (diffDays <= 31) return { allowed: [1, 2, 3, 4], def: 1 };
        if (diffDays <= 92) return { allowed: [1, 2, 3, 4, 5], def: 1 };

        return { allowed: [3, 4, 5], def: 3 };
    }

    useEffect(() => {
        if (!formData.dateRange) return;

        const g = generateGrouping(formData.dateRange, appliedModuleSelection);
        setAllowedGroupings(g.allowed);
        setGrouping(g.def);
    }, [formData.dateRange, appliedModuleSelection]);

    function createRadioInput(station, daypart = null, demo = null, mode = 0) {
        if (mode === 1) {
            // Listener mode
            return {
                radio_id: station?.radio_id ?? null,
                demo_id: demo?.demo_id ?? null,
                daypart_id: daypart?.daypart_id ?? null,
                data_type: demo?.data_type ?? null,
                listener_group: demo?.listener_group ?? null,
            };
        }

        // Session mode
        return {
            radio_id: station?.radio_id ?? null,
            demo_id: demo?.demo_id ?? null,
        };
    }
    async function getData(lastYearAverage, grouping, excludeMode, moduleType, onlySubLoading = false) {
        if (onlySubLoading) setSubLoading(true);

        const start_datetime = moment(formData.dateRange[0]).startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        const end_datetime = moment.min([moment(formData.dateRange[1]).endOf("day"), moment(latestCalculatedDate)]).format("YYYY-MM-DDTHH:mm:ss");

        let radios;

        if (moduleType === 0) {
            // Session mode
            radios = [
                createRadioInput(formData.station[0], null, formData.demo[0], 0),
                ...Array(extraStationsCount)
                    .fill()
                    .map((_, i) => createRadioInput(formData[`extraStation${i}`][0], null, formData[`extraDemo${i}`][0], 0))
                    .filter(r => r.radio_id && r.demo_id),
            ];
        } else {
            // Listener mode
            radios = [
                createRadioInput(formData.station[0], formData.daypart[0], formData.demo[0], 1),
                ...Array(extraStationsCount)
                    .fill()
                    .map((_, i) =>
                        createRadioInput(formData[`extraStation${i}`][0], formData[`extraDaypart${i}`]?.[0] ?? null, formData[`extraDemo${i}`][0], 1)
                    )
                    .filter(r => r.radio_id && r.demo_id && r.daypart_id),
            ];
        }

        const input = {
            radios,
            start_datetime,
            end_datetime,
            grouping,
            include_averages: true,
            last_year_as_averages: lastYearAverage,
            exclude_mode: excludeMode,
        };

        if (moduleType === 0) {
            // Session
            input.start_hour = formData.hourRange[0];
            input.end_hour = formData.hourRange[1] - 1;
            input.weekdays = formData.days;
        }

        const endpoint = moduleType === 0 ? "/tsl/session/" : "/tsl/listener/";
        await fetchData(endpoint, input);

        if (onlySubLoading) setSubLoading(false);
    }

    async function handleFormSubmit(event) {
        event.preventDefault();
        setAppliedModuleSelection(moduleSelection);

        const g = generateGrouping(formData.dateRange, moduleSelection);
        setAllowedGroupings(g.allowed);
        setGrouping(g.def);

        await getData(lastYearAverages, g.def, excludeMode, moduleSelection, false);
        advancedInputRef.current.closeAdvanced();
        await saveFormData();
    }

    async function handleAverageChange(value) {
        setLastYearAverages(value);
        await getData(value, grouping, excludeMode, appliedModuleSelection, true);
    }
    async function handleGroupingChange(value) {
        setGrouping(value);
        await getData(lastYearAverages, value, excludeMode, appliedModuleSelection, true);
    }

    async function handleExcludeModeChange(value) {
        setExcludeMode(value);
        await getData(lastYearAverages, grouping, value, appliedModuleSelection, true);
    }
    async function handleModuleSelection(value) {
        setModuleSelection(value);
    }

    return (
        <ModulePage>
            <ModulePage.Title>
                TSL
                <Stack className="bg-white  pt-3" direction="horizontal">
                    <ToggleButtonGroup type={"radio"} name="module-selection" value={moduleSelection} onChange={handleModuleSelection} className="ra-switcher">
                        <ToggleButton id={`${moduleName}-session-selection`} variant={"ra-dark-blue-outline"} value={0}>
                            Session
                        </ToggleButton>
                        <ToggleButton id={`${moduleName}-listeners-selection`} className=" text-nowrap" variant={"ra-dark-blue-outline"} value={1}>
                            Listener
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
            </ModulePage.Title>
            <ModulePage.InputSelection
                handleFormSubmit={handleFormSubmit}
                disabled={
                    !formData.station.length ||
                    !formData.demo.length ||
                    !formData.hourRange.length ||
                    !formData.dateRange ||
                    !formData.days.length ||
                    formData.hourRange[0] === formData.hourRange[1] ||
                    (moduleSelection === 1 && !formData.daypart.length) ||
                    (formData.extraStation0.length && !formData.extraDemo0.length && !formData.extraDaypart0.length) ||
                    (formData.extraStation1.length && !formData.extraDemo1.length && !formData.extraDaypart1.length) ||
                    (formData.extraStation2.length && !formData.extraDemo2.length && !formData.extraDaypart2.length)
                }>
                <InputSelection.Main>
                    <Suspense>
                        <Await resolve={userInfo}>
                            {resolvedUser => (
                                <StationPicker
                                    formData={formData}
                                    setFormData={setFormData}
                                    name={moduleName}
                                    moduleFilterFunction={x => x.module_id === 6}
                                    filterFunction={x => resolvedUser.data.superuser || !x.mtt_only}
                                    loadOnChange={loadFormData}
                                />
                            )}
                        </Await>
                    </Suspense>
                    <Suspense>
                        <Await resolve={radiosModuleSettings}>
                            {resolvedModuleSettings => (
                                <DemoPicker
                                    formData={formData}
                                    setFormData={setFormData}
                                    name={moduleName}
                                    disabled={!formData.station.length}
                                    filterFunction={x => {
                                        if (!formData.station.length) return false;
                                        const station = formData.station[0];
                                        const hasListenerGroupModule = resolvedModuleSettings.data.find(
                                            r => r.radio_id === station.radio_id && r.module_id === 7 && r.enabled
                                        );
                                        return (
                                            (x.data_type === 1 && station.stream && (hasListenerGroupModule ? true : x.listener_group === 0)) ||
                                            (x.data_type === 2 && station.ppm)
                                        );
                                    }}
                                />
                            )}
                        </Await>
                    </Suspense>
                    <DatesPicker formData={formData} setFormData={setFormData} predefinedRanges={predefinedRanges} latestAvailableDate={latestCalculatedDate} />
                    <DaypartPicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Main>
                <Suspense>
                    <Await resolve={radiosModuleSettings}>
                        {resolvedModuleSettings => (
                            <InputSelection.Advanced moduleName={moduleName} ref={advancedInputRef}>
                                {moduleSelection === 0 && (
                                    <>
                                        <DaysPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                        <HoursPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                        <PerspectivePicker formData={formData} setFormData={setFormData} name={moduleName} />
                                    </>
                                )}

                                {Array(extraStationsCount)
                                    .fill()
                                    .map((_, i) => {
                                        const station = formData[`extraStation${i}`][0];
                                        return (
                                            <Col xs={12} key={i}>
                                                <Row>
                                                    <StationPicker
                                                        formData={formData}
                                                        setFormData={setFormData}
                                                        name={moduleName}
                                                        moduleFilterFunction={x => x.module_id === 6}
                                                        formKey={`extraStation${i}`}
                                                        demoKey={`extraDemo${i}`}
                                                        label={`Station #${i + 2}`}
                                                        daypartKey={`extraDaypart${i}`}
                                                    />

                                                    <DemoPicker
                                                        formData={formData}
                                                        setFormData={setFormData}
                                                        name={moduleName}
                                                        disabled={!formData[`extraStation${i}`].length || formData[`extraStation${i}`][0]?.mtt_only}
                                                        formKey={`extraDemo${i}`}
                                                        stationKey={`extraStation${i}`}
                                                        label={`Demographic #${i + 2}`}
                                                        filterFunction={x => {
                                                            if (!station) return false;
                                                            const hasListenerGroupModule = resolvedModuleSettings.data.find(
                                                                r => r.radio_id === station.radio_id && r.module_id === 7 && r.enabled,
                                                            );
                                                            return (
                                                                (x.data_type === 1 &&
                                                                    station.stream &&
                                                                    (hasListenerGroupModule ? true : x.listener_group === 0)) ||
                                                                (x.data_type === 2 && station.ppm)
                                                            );
                                                        }}
                                                    />
                                                    {moduleSelection === 1 && (
                                                        <DaypartPickerTsl
                                                            formData={formData}
                                                            setFormData={setFormData}
                                                            disabled={!formData[`extraStation${i}`].length || formData[`extraStation${i}`][0]?.mtt_only}
                                                            name={moduleName}
                                                            filterFunction={x => {
                                                                if (!station) return false;
                                                                return x.radio_id === station?.radio_id;
                                                            }}
                                                            formKey={`extraDaypart${i}`}
                                                            stationKey={`extraStation${i}`}
                                                            label={`Daypart #${i + 2}`}
                                                        />
                                                    )}
                                                </Row>
                                            </Col>
                                        );
                                    })}
                            </InputSelection.Advanced>
                        )}
                    </Await>
                </Suspense>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading && !subLoading} errorCode={errorCode}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"TSL"}
                        whatList={[
                            "TSL per session and TSL per listener as an easy to read graph",
                            "Choose different dayparts, timeframes, and listener groups",
                            "View multiple stations or listener groups in the same graph",
                        ]}
                        knowList={[
                            { text: "Grouping can be changed on the fly. Choose minutes, hours, days, weeks, or months with a click" },
                            { text: "Compare to last 13 weeks for short term trends, or last year’s average for long term development" },
                            { text: "TSL per listener only works on a weekly basis" },
                        ]}
                        relatedLinks={[]}
                        // relatedLinks={[
                        //     {
                        //         title: "An Apple a Day",
                        //         url: "https://radioanalyzer.com/blog/an-apple-a-day",
                        //     },
                        // ]}
                        // videoSrc="https://www.youtube.com/embed/qCjHAVycInE?si=_AoMoULxEeag-jLO"
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        <div ref={container}>
                            <DescriptionWithDownloadRow csvHeaders={csvHeaders} data={csvData} filename={fileName} screenshotContainer={container}>
                                <DataDescription>
                                    <DataDescription.Stations radioIds={data?.radios.map(x => x.radio_id)} />
                                    <DataDescription.DateRange startDate={data?.start_datetime} endDate={data?.end_datetime} />
                                    {appliedModuleSelection === 0 && (
                                        <>
                                            <DataDescription.HourRange startHour={data?.start_hour} endHour={data?.end_hour} />
                                            <DataDescription.Perspective days={data?.weekdays} />
                                        </>
                                    )}
                                    <strong>{appliedModuleSelection === 0 ? "TSL per Session" : "TSL per Listener"}</strong>
                                </DataDescription>
                            </DescriptionWithDownloadRow>
                            <Stack direction="horizontal" gap={1} className=" flex-wrap column-gap-4 row-gap-2 mb-2">
                                <Stack direction="horizontal" gap={2}>
                                    <span>
                                        Average
                                        <RATooltip>
                                            <p>
                                                <strong>13 weeks </strong>- Avg. of latest 13 weeks from the selected timeframe, holidays and corrupted days are
                                                excluded.
                                            </p>
                                            <p>
                                                <strong>Last year</strong> - The same weekday in the same calendar week of the previous year, in the HEALTH
                                                module, corrupted days are excluded
                                            </p>
                                        </RATooltip>
                                    </span>
                                    <ToggleButtonGroup
                                        type={"radio"}
                                        name="last-year-average"
                                        value={lastYearAverages}
                                        onChange={handleAverageChange}
                                        className="ra-switcher">
                                        <ToggleButton id={`${moduleName}-last-year-averages-off`} variant={"ra-dark-blue-outline"} value={false}>
                                            13 Weeks
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`${moduleName}-last-year-averages-on`}
                                            className=" text-nowrap"
                                            variant={"ra-dark-blue-outline"}
                                            value={true}>
                                            Last Year
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                                <Stack className="d-flex flex-md-row flex-column" gap={2}>
                                    <span className="justify-self-center align-self-md-center">Grouping </span>
                                    <ToggleButtonGroup
                                        type={"radio"}
                                        name="grouping"
                                        value={grouping}
                                        onChange={handleGroupingChange}
                                        className="ra-switcher flex-wrap flex-md-nowrap">
                                        <ToggleButton
                                            id={`${moduleName}-grouping-hours`}
                                            variant="ra-dark-blue-outline"
                                            value={1}
                                            disabled={!allowedGroupings.includes(1)}
                                            style={{ display: appliedModuleSelection === 0 ? "block" : "none" }}>
                                            Hours
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`${moduleName}-grouping-3-hours`}
                                            variant="ra-dark-blue-outline"
                                            value={2}
                                            disabled={!allowedGroupings.includes(2)}
                                            style={{ display: appliedModuleSelection === 0 ? "block" : "none" }}>
                                            3 Hours
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`${moduleName}-grouping-days`}
                                            variant="ra-dark-blue-outline"
                                            value={3}
                                            disabled={!allowedGroupings.includes(3)}
                                            style={{ display: appliedModuleSelection === 0 ? "block" : "none" }}>
                                            Days
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`${moduleName}-grouping-weeks`}
                                            variant={"ra-dark-blue-outline"}
                                            value={4}
                                            disabled={!allowedGroupings.includes(4)}>
                                            Weeks
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`${moduleName}-grouping-months`}
                                            variant={"ra-dark-blue-outline"}
                                            value={5}
                                            disabled={!allowedGroupings.includes(5)}>
                                            Months
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                                <Stack className="d-flex flex-md-row flex-column" gap={2}>
                                    <span className="justify-self-center align-self-md-center ">
                                        Exclude
                                        <RATooltip>
                                            <p>
                                                The selection excludes <strong>from the averages only</strong>
                                            </p>
                                            <p>
                                                <strong>Holidays</strong> - all public or school holidays from the broadcast area
                                            </p>
                                            <p>
                                                <strong>Corrupted</strong> - days with stream drops or technical issues
                                            </p>
                                        </RATooltip>
                                    </span>
                                    <ToggleButtonGroup
                                        type={"radio"}
                                        name="exclude_mode"
                                        value={excludeMode}
                                        onChange={handleExcludeModeChange}
                                        className="ra-switcher flex-wrap flex-md-nowrap">
                                        <ToggleButton id={`${moduleName}-exclude-grouping-both`} variant={"ra-dark-blue-outline"} value={"both"}>
                                            Both
                                        </ToggleButton>
                                        <ToggleButton id={`${moduleName}-exclude-grouping-holidays`} variant={"ra-dark-blue-outline"} value={"holidays"}>
                                            Holidays
                                        </ToggleButton>
                                        <ToggleButton id={`${moduleName}-exclude-grouping-corrupted`} variant={"ra-dark-blue-outline"} value={"corrupted"}>
                                            Corrupted
                                        </ToggleButton>
                                        <ToggleButton id={`${moduleName}-exclude-grouping-none`} variant={"ra-dark-blue-outline"} value={"none"}>
                                            None
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                            </Stack>
                            {subLoading ? (
                                <div className=" d-flex flex-grow-1">
                                    <LoadingIndicator width={75} />
                                </div>
                            ) : (
                                <TslChart
                                    data={data.data}
                                    grouping={grouping}
                                    resolvedRadios={resolvedRadios}
                                    resolvedDemos={resolvedDemos}
                                    moduleSelection={appliedModuleSelection}
                                />
                            )}
                        </div>
                    </>
                )}
            </ModulePage.DataContent>
        </ModulePage>
    );
}
